import { config } from '@abyss/web/tools/config';

import { Constants } from '../../common/Constants';
import { ChipState } from '../../store/useChipStore/chipStore';
import { DateTime } from '../../utils/DateTime';
import { getProviderDetailsRouteUrl } from '../../utils/providerDetails.utils';
import { fetchMemberStateFromMapBox } from '../../utils/user.utils';
import { getLanguage } from '../ProviderSearch/context/Internationalization/helpers';
import { toastErrorStyle } from './GuestMultiplePlan.style';

export const getLobForPortalData = (lob: string): string => {
  switch (lob) {
    case 'EI':
      return 'E&I';
    case 'MR':
      return 'M&R';
    case 'CS':
      return 'C&S';
    case 'IFP':
      return 'IFP';
    case 'SHOP':
      return 'SHOP';
    default:
      return '';
  }
};

export const getPopulation = (
  coverageType: string,
  lob: string,
  searchDirectory: string
): string => {
  if (lob === 'CS') return 'Medicaid';
  if (lob === 'IFP') return 'IFP';
  if (coverageType === 'M' && lob === 'MR') return 'COSMOS';
  if (coverageType === 'M' && lob === 'EI') {
    switch (searchDirectory) {
      case Constants.MEMBER_TYPES.USP:
        return Constants.MEMBER_TYPES.USP;
      case Constants.MEMBER_TYPES.SIERRA:
        return Constants.MEMBER_TYPES.SIERRA;
      default:
        return Constants.MEMBER_TYPES.UNET;
    }
  }
  return '';
};

export const buildPortalData = (
  selectedPlan: any,
  lob: string,
  coverageType: string,
  stateCode: string,
  planYear?: string,
  signInUrl?: string,
  planCategory?: string
): any => {
  const { planName, guestPlanIdentifier } = selectedPlan;
  const year =
    planYear && selectedPlan.years?.length > 1
      ? selectedPlan.years.find((year) => year.planYear === planYear)
      : selectedPlan.years?.[0] ?? {};

  const { reciprocityId } = year;
  const searchDirectory = selectedPlan?.searchDirectory || null;
  const population = getPopulation(coverageType, lob, searchDirectory);
  const lobForGuest = getLobForPortalData(lob);
  const currentYear =
    DateTime.getCurrentDateTimeWithCentralOffset().getFullYear();
  const selectedYear = planYear ?? currentYear;
  const coverageEffStartDate = `${selectedYear}-01-01`;
  const coverageEffEndDate = `${selectedYear}-12-31`;
  return {
    lineOfBusiness: lobForGuest,
    reciprocityId: reciprocityId || '52',
    population,
    coverageTypeCode: coverageType || 'M',
    coverageEffStartDate,
    coverageEffEndDate,
    stateCode,
    planName,
    planYear: selectedYear,
    signInUrl,
    searchDirectory,
    planCategory,
    guestPlanIdentifier
  };
};

export const LOB_MAPPING = {
  EI: [
    { coverage: 'M', locationRequirement: 'n/a' },
    { coverage: 'B', locationRequirement: 'n/a' },
    { coverage: 'D', locationRequirement: 'state' },
    { coverage: 'V', locationRequirement: 'n/a' },
  ],
  MR: [
    { coverage: 'M', locationRequirement: 'county' },
    { coverage: 'B', locationRequirement: 'county' },
    { coverage: 'D', locationRequirement: 'state' },
    { coverage: 'V', locationRequirement: 'n/a' },
  ],
  CS: [
    { coverage: 'M', locationRequirement: 'state' },
    { coverage: 'B', locationRequirement: 'state' },
    { coverage: 'D', locationRequirement: 'state' },
  ],
  IFP: [
    { coverage: 'M', locationRequirement: 'state' },
    { coverage: 'B', locationRequirement: 'state' },
    { coverage: 'D', locationRequirement: 'n/a' },
    { coverage: 'V', locationRequirement: 'n/a' },
  ],
  SHOP: [
    { coverage: 'M', locationRequirement: 'state' },
    { coverage: 'B', locationRequirement: 'state' },
    { coverage: 'D', locationRequirement: 'n/a' },
    { coverage: 'V', locationRequirement: 'n/a' },
  ],
};

export const getLocationRequirement = (
  lob: string,
  coverageType: string
): string => {
  const lobConfig = LOB_MAPPING[lob];

  if (!lobConfig) {
    throw new Error(`LOB ${lobConfig} is not defined in LOB_MAPPING.`);
  }

  const coverageConfig = lobConfig.find(
    (item) => item.coverage === coverageType
  );

  if (!coverageConfig) {
    throw new Error(
      `Coverage type ${coverageType} is not defined for LOB ${lobConfig}.`
    );
  }

  return coverageConfig.locationRequirement;
};

export const getChipValue = (coverageType: string): string => {
  let chipValue = 'All';
  if (coverageType === 'B') {
    chipValue = 'Behavioral%20Health';
  } else if (coverageType === 'D') {
    chipValue = 'Dental';
  } else if (coverageType === 'V') {
    chipValue = 'Vision';
  }
  return chipValue;
};

const PAGE_NAME_MAPPING: {
  [key: string]: string | { location: string; results: string };
} = {
  M: { location: 'medical', results: 'medical search results' },
  B: {
    location: 'behavioral health',
    results: 'behavioral health search results',
  },
  D: { location: 'dental', results: 'dental search results' },
  V: { location: 'vision', results: 'vision search results' },
};

export const getGuestLocationPageName = (coverage: string): string => {
  const mapping = PAGE_NAME_MAPPING[coverage];
  if (typeof mapping === 'object') {
    return mapping.location;
  }
  return 'medical';
};

export const getGuestResultsPageName = (coverage: string): string => {
  const mapping = PAGE_NAME_MAPPING[coverage];
  if (typeof mapping === 'object') {
    return mapping.results;
  }
  return 'medical search results';
};

export const toSlug = (input: string | undefined | null): string =>
  input ? input.replace(/\s+/g, '-').toLowerCase() : '';

export const shouldSkipPlanSelection = (
  availablePlansLength: number
): boolean => availablePlansLength === 1;

export const filterUniquePlanNames = <T extends { planName?: string }>(
  allPlanNames: T[] | undefined,
  uniquePlanNames: Set<string>
): T[] => {
  if (!allPlanNames) return [];

  return allPlanNames.filter((name) => {
    const planName = name?.planName;
    if (planName && !uniquePlanNames.has(planName)) {
      uniquePlanNames.add(planName);
      return true;
    }
    return false;
  });
};

export function getCoverageTypeString(coverageType: string): string {
  switch (coverageType) {
    case 'M':
      return 'medical';
    case 'B':
      return 'behavioral';
    case 'D':
      return 'dental';
    case 'V':
      return 'vision';
    default:
      return '';
  }
}

export function extractZipCodeFromPlaceName(placeName: string): string | null {
  if (!placeName) return null;
  // Match a 5-digit zipcode number at the end of a comma-separated segment
  const match = placeName.match(/\b(\d{5})(?:-[0-9]{4})?\b/);
  return match ? match[1] : null;
}

export function getYearIndex(
  years: Array<{ planYear: string }>,
  title: string
): number {
  if (!Array.isArray(years) || !title) return 0;
  const idx = years.findIndex((year) => year.planYear === title);
  return idx === -1 ? 0 : idx;
}

export async function getZipCodeFromLatLong(
  apiUrl,
  longitude,
  latitude,
  mapKey
) {
  const data = await fetchMemberStateFromMapBox(
    apiUrl,
    longitude,
    latitude,
    mapKey
  );
  const placeName = data.features?.[0]?.place_name;
  return extractZipCodeFromPlaceName(placeName) ?? '';
}

/**
 * Determines if a plan should be treated as an external link.
 * Returns true if any year in the plan has groupDriven=true or a non-null guestPath.
 */
export function IsExternallink(plan?: {
  years?: Array<{ groupDriven?: boolean; guestPath?: string | null }>;
}): boolean {
  if (!plan?.years || !Array.isArray(plan.years)) return false;
  return plan.years.some((year) => year.groupDriven || !!year.guestPath);
}

export async function handleGuestPlanNavigation({
  getFpcGuestToken,
  selectedPlan,
  planYear,
  fipscode,
  FPC_BASE_URL,
  zipcode,
  chipValue,
  coverageType,
  navigate,
  lob,
  stateCode,
  longitude,
  latitude,
  mapKey,
  signInUrl,
  toast,
  toastMessageForFpcTokenError = '',
  isDual = false,
  planCategory,
}) {
  const yearIndex = getYearIndex(selectedPlan?.years, planYear);
  const yearObj = selectedPlan?.years?.[yearIndex];
  if (yearObj?.groupDriven) {
    const apiUrl = config('API_GATEWAY_URL');
    let urlZipCode = zipcode;
    const language = getLanguage()?.code;
    const planIdentifier =
      typeof selectedPlan?.planIdentifier === 'string'
        ? selectedPlan?.planIdentifier.replace(/-/g, '')
        : '';
    if (!urlZipCode) {
      urlZipCode = await getZipCodeFromLatLong(
        apiUrl,
        longitude,
        latitude,
        mapKey
      );
    }
    const token = await getFpcGuestToken({
      variables: {
        planName: selectedPlan?.planName,
        delsys: planIdentifier,
        coverageType: getCoverageTypeString(coverageType),
        language: language || 'en',
        clusterKey: yearObj?.clusterKey || '',
        fipsCode: fipscode,
        partnerId: 'uhc',
      },
    }).then(
      async (response: { data: { getFpcGuestToken: { token: string } } }) =>
        response?.data?.getFpcGuestToken?.token || ''
    );
    if (!token) {
      toast.show({
        message: toastMessageForFpcTokenError,
        autoClose: 30000,
        css: toastErrorStyle,
      });
      return;
    }
    window.open(
      `${FPC_BASE_URL}/guest/${token}?planyear=${planYear}&zipCode=${urlZipCode}`,
      '_blank'
    );
    return;
  }
  if (yearObj?.guestPath) {
    const url = yearObj.guestPath;
    window.open(url, '_blank');
    return;
  }
  sessionStorage.setItem(
    Constants.STORAGE_KEYS.SESSION.SELECTED_PLAN_DETAILS,
    JSON.stringify({
      ...buildPortalData(
        selectedPlan,
        lob,
        coverageType,
        stateCode || '',
        planYear,
        undefined,
        planCategory
      ),
      isDual,
      selectedPlan,
    })
  );
  sessionStorage.setItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA,
    JSON.stringify({
      selectedPlan,
      lob,
      coverageType: coverageType || 'M',
      stateCode: stateCode || '',
    })
  );
  navigate(`/browse?chipValue=${chipValue}&coverageType=${coverageType}`, {
    state: buildPortalData(
      selectedPlan,
      lob,
      coverageType,
      stateCode || '',
      planYear,
      signInUrl,
      planCategory
    ),
  });
}

export const redirectToGuestSignIn = (
  // two supported call signatures:
  // 1) (signInUrl: string, locale?: string)
  // 2) (portalBasePath: string, signInUrl: string, chipStore: ChipState, providerId: string, selectedId: string, showDirection: boolean, isTieredDataCard: boolean, sectionType: string, choosePCPFlag: boolean, locale?: string)
  ...args: any[]
): void => {
  // If called with more than 2 args, treat as scheduling-callback style and build callback URL
  if (args.length > 2) {
    const [
      portalBasePath,
      signInUrl,
      chipStore,
      providerId = '',
      selectedId,
      showDirection,
      isTieredDataCard = false,
      sectionType,
      choosePCPFlag = false,
      locale = 'en-US',
    ] = args;

    const providerDetailsRoute = getProviderDetailsRouteUrl(
      chipStore,
      providerId,
      selectedId,
      showDirection,
      isTieredDataCard,
      sectionType,
      choosePCPFlag
    );

    const targetPath = `&initiateDocasapSession=true`;
    const callBackUrl = `${portalBasePath}${providerDetailsRoute}${targetPath}`;
    const encodedCallBackUrl = encodeURIComponent(
      `${providerDetailsRoute}${targetPath}`
    );

    const URL = signInUrl
      ?.replace(/ENCODED_LANDING_PAGE/g, encodedCallBackUrl)
      .replace(/LANDING_PAGE/g, callBackUrl)
      .replace(/LOCALE_VALUE/g, locale);
    window.location.href = URL;
    return;
  }

  // Fallback to simple sign-in redirect: (signInUrl, locale?)
  const [signInUrl, locale = 'en-US'] = args as [string, string?];
  const URL = signInUrl
    ?.replace(/ENCODED_LANDING_PAGE/g, '?')
    .replace(/LOCALE_VALUE/g, locale);
  window.location.href = URL;
};

export const redirectToGuestSignInSchedulingCallback = (
  portalBasePath: string,
  signInUrl: string,
  chipStore: ChipState,
  providerId: string = '',
  selectedId: string,
  showDirection: boolean,
  isTieredDataCard: boolean = false,
  sectionType: string,
  choosePCPFlag: boolean = false,
  locale: string = 'en-US'
): void => {
  const providerDetailsRoute = getProviderDetailsRouteUrl(
    chipStore,
    providerId,
    selectedId,
    showDirection,
    isTieredDataCard,
    sectionType,
    choosePCPFlag
  );

  const targetPath = `&initiateDocasapSession=true`;
  const callBackUrl = `${portalBasePath}${providerDetailsRoute}${targetPath}`;
  const encodedCallBackUrl = encodeURIComponent(
    `${providerDetailsRoute}${targetPath}`
  );

  const URL = signInUrl
    ?.replace(/ENCODED_LANDING_PAGE/g, encodedCallBackUrl)
    .replace(/LANDING_PAGE/g, callBackUrl)
    .replace(/LOCALE_VALUE/g, locale);
  window.location.href = URL;
};
